import React, { useState } from "react";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { MapPin, Star, Trash2, Plus } from "lucide-react";

export function SitesDialog({ open, onClose, sites = [], onChanged }) {
    const { hasPermission } = useApp();
    const isAdmin = hasPermission("admin");
    const [newName, setNewName] = useState("");

    const run = async (fn, msg) => {
        try { await fn(); toast.success(msg); onChanged?.(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const add = () => {
        if (!newName.trim()) return;
        run(() => api.createPrinterSite({ name: newName.trim() }), "Site ajouté").then(() => setNewName(""));
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPin className="w-4 h-4 text-teal-600" />Sites</DialogTitle></DialogHeader>
                <p className="text-xs text-gray-500 -mt-2">Les sites servent à localiser les imprimantes et les stocks de consommables. Le site par défaut est pré-sélectionné à la création.</p>
                <div className="space-y-1.5 py-1" data-testid="sites-list">
                    {sites.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-gray-50" data-testid={`site-row-${s.id}`}>
                            <Input defaultValue={s.name} disabled={!isAdmin} className="h-8 text-sm flex-1"
                                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== s.name) run(() => api.updatePrinterSite(s.id, { name: v }), "Site renommé"); }}
                                data-testid={`site-name-${s.id}`} />
                            <button onClick={() => isAdmin && !s.is_default && run(() => api.updatePrinterSite(s.id, { is_default: true }), `${s.name} est le site par défaut`)}
                                className={`p-1 rounded ${s.is_default ? "text-amber-500" : "text-gray-300 hover:text-amber-400"}`} title={s.is_default ? "Site par défaut" : "Définir par défaut"} disabled={!isAdmin}
                                data-testid={`site-default-${s.id}`}>
                                <Star className={`w-4 h-4 ${s.is_default ? "fill-amber-400" : ""}`} />
                            </button>
                            {isAdmin && (
                                <button onClick={() => window.confirm(`Supprimer le site "${s.name}" ?`) && run(() => api.deletePrinterSite(s.id), "Site supprimé")}
                                    className="p-1 text-gray-300 hover:text-red-500" title="Supprimer" data-testid={`site-delete-${s.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                {isAdmin && (
                    <div className="flex items-center gap-2">
                        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nouveau site (ex : GA24)" className="h-8 text-sm" onKeyDown={(e) => e.key === "Enter" && add()} data-testid="site-new-name" />
                        <Button size="sm" className="h-8" onClick={add} disabled={!newName.trim()} data-testid="site-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter</Button>
                    </div>
                )}
                <DialogFooter><Button variant="outline" onClick={onClose}>Fermer</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
